// raisedbeef.com billing on the account page (RAISEDBEEF_COM_BILLING_PLAN.md
// §4 and §8; owner, 2026-10-09: "build the site and the app together" — sell
// on the website with Stripe, the app's Subscribe button opens this page).
//
// Shows where the person stands — the 30 free days, or their plan and when it
// renews — and either the plan to buy or Manage billing (the Stripe Customer
// Portal: cancel, card, monthly ↔ yearly). ONE PLAN since 2026-10-25: the
// price and the bullets come from src/lib/sitePlan.ts.
//
// Everything about money happens on Stripe's pages; this page only asks for a
// Checkout or Portal link (rb-site-checkout, rb-site-portal) and follows it.

import { useEffect, useState } from "react";

import { supabase } from "@/integrations/supabase/client";
import { reportBillingToMeta } from "@/lib/metaConversions";
import { daysLeft as siteDaysLeft, type Level } from "@/lib/siteAccess";
import { CLARA_PRO_ADDON, LEGACY_BASIC, SITE_PLAN } from "@/lib/sitePlan";

export { CLARA_PRO_ADDON };

interface Billing {
  level: Level;
  trial_ends_at: string | null;
  /** The subscription's tier word. "basic" is the one grandfathered
   *  subscription (ONE PLAN, 2026-10-25): it reads as the plan everywhere. */
  tier: "basic" | "premium" | null;
  interval: "monthly" | "annual" | null;
  status: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  has_customer: boolean;
  /** A free plan from /manage/subscribers, only while it lasts. */
  comp_tier?: "basic" | "premium" | null;
  comp_until?: string | null;
  comp_clara_pro?: boolean;
}

/** What a live subscription costs a month or a year — the plan's price, or
 *  the grandfathered Basic rate for that one row. Money only (Meta, the admin
 *  page); it is never shown as something to buy. */
export function paidPrice(tier: "basic" | "premium", interval: "monthly" | "annual" | null): number {
  const p = tier === "basic" ? LEGACY_BASIC : SITE_PLAN;
  return interval === "annual" ? p.annual : p.monthly;
}

interface Addon { status: string; interval: "monthly" | "annual" | null; current_period_end: string | null; cancel_at_period_end: boolean }

const day = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";

/** Whole days left on the free month — the same count as the gate's
 *  (src/lib/siteAccess.ts), so the page and the gate cannot disagree. */
export function daysLeft(iso: string | null, now = Date.now()): number | null {
  return siteDaysLeft(iso, new Date(now));
}

/** "Member, free" / "Member, free until December 31, 2026". */
export function freePlanLine(until: string | null | undefined): string {
  return `${SITE_PLAN.short}, free${until ? ` until ${day(until)}` : ""}`;
}

/** Where a subscriber starts (owner, 2026-10-12: "i just want them to know
 *  what to do from this page"). The indexes first. */
export const START_HERE: ReadonlyArray<{ label: string; href: string; star?: boolean }> = [
  { label: "Cattle Market Central Indexes", href: "/index", star: true },
  { label: "Weekly Averages", href: "/weekly-averages" },
  { label: "Weight Breaks", href: "/quotes-board" },
  { label: "Live barn tickers", href: "/barns" },
  { label: "Leaderboard", href: "/leaderboard" },
  { label: "States", href: "/states" },
  { label: "Sale reports", href: "/reports" },
  { label: "Toppers", href: "/toppers" },
];

/** Plain words for whatever the functions say went wrong. */
export function billingError(code: string | undefined): string {
  switch (code) {
    case "not_configured": return "Subscriptions open soon. Nothing has been charged.";
    case "already_subscribed": return "You already have that — use Manage billing to change it.";
    case "premium_first": return `The Clara Pro add-on goes on top of ${SITE_PLAN.short}. Subscribe first.`;
    case "sign_in_first": return "Please sign in again, then try once more.";
    default: return "Couldn't reach checkout. Nothing has been charged — try again in a minute.";
  }
}

/** `onWall`: drawn inside the paywall card, which says "Your free month has
 *  ended" in its own heading — so the status line here is left out. */
export default function SiteBilling({ onWall = false }: { onWall?: boolean } = {}) {
  const [b, setB] = useState<Billing | null>(null);
  const [addon, setAddon] = useState<Addon | null>(null);
  const [annual, setAnnual] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const returned = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("checkout") : null;

  useEffect(() => {
    let live = true;
    const load = () => supabase.rpc("rb_site_my_billing" as never).then(({ data }) => {
      if (live && data) {
        const d = data as unknown as Billing;
        setB(d);
        // Tells Meta of a confirmed trial or a confirmed paid plan, once each
        // (src/lib/metaConversions.ts). Read-only: it changes nothing here.
        void reportBillingToMeta(d, d.tier ? paidPrice(d.tier, d.interval) : 0, returned === "success");
      }
      return data as unknown as Billing | null;
    });
    // ⚠️ BACK FROM CHECKOUT, THE PLAN CAN BE A FEW SECONDS BEHIND THE PERSON:
    // Stripe sends them here and tells the website separately. Ask again every
    // two seconds for up to a minute, so the welcome replaces "being set up"
    // the moment it lands, without a reload.
    let tries = 0;
    let timer: number | undefined;
    const poll = () => void load().then((d) => {
      const has = d?.tier && ["trialing", "active", "past_due"].includes(String(d.status));
      if (live && returned === "success" && !has && ++tries < 30) timer = window.setTimeout(poll, 2000);
    });
    poll();
    // rb_site_addons is newer than the generated types; RLS returns only the
    // person's own row.
    const addons = supabase as unknown as { from(t: "rb_site_addons"): { select(c: string): { eq(c: "addon", v: string): { maybeSingle(): Promise<{ data: Addon | null }> } } } };
    void addons.from("rb_site_addons").select("status, interval, current_period_end, cancel_at_period_end").eq("addon", "clara_pro").maybeSingle()
      .then(({ data }) => { if (live) setAddon(data); });
    return () => { live = false; window.clearTimeout(timer); };
  }, [returned]);

  const go = async (fn: "rb-site-checkout" | "rb-site-portal", body: Record<string, string>) => {
    setBusy(fn + (body.tier ?? body.addon ?? "")); setError(null);
    const { data, error: err } = await supabase.functions.invoke(fn, { body: { ...body, origin: window.location.origin } });
    const d = (data ?? {}) as { url?: string; error?: string };
    if (err || !d.url) {
      // supabase-js puts the function's JSON on the error's context for non-2xx.
      let code = d.error;
      try { code = code ?? (await (err as { context?: Response })?.context?.json?.())?.error; } catch { /* keep default */ }
      setError(billingError(code));
      setBusy(null);
      return;
    }
    window.location.href = d.url;
  };

  if (!b) return null;
  const paying = b.tier && ["trialing", "active", "past_due"].includes(String(b.status));
  const left = daysLeft(b.trial_ends_at);

  return (
    <section className="rbacct-billing" aria-label="Your plan">
      {returned === "success" && !paying && <p className="rbacct-notice">Thank you — your plan is being set up. It can take a minute to show here.</p>}
      {returned === "cancel" && <p className="rbacct-note">Checkout closed. Nothing was charged.</p>}

      {b.level === "admin" ? (
        <p className="rbacct-body">Administrator — everything is open to you.</p>
      ) : paying ? (
        <>
          <div className="rbacct-on" data-welcome={returned === "success" ? "1" : undefined}>
            {returned === "success" && <span className="rbacct-tick" aria-hidden="true">✓</span>}
            <p className="rbacct-plan">
              {returned === "success" ? "Welcome. You're on " : null}
              <b>{SITE_PLAN.name}</b> · {b.interval === "annual" ? "yearly" : "monthly"}
              {b.status === "past_due" ? <em className="rbacct-warn"> · payment due</em> : null}
              <small className="rbacct-fine">
                {b.cancel_at_period_end ? `Ends ${day(b.current_period_end)} — it won't renew.` : `Renews ${day(b.current_period_end)}.`}
              </small>
              {b.comp_tier && <small className="rbacct-fine">Also: {freePlanLine(b.comp_until)}.</small>}
            </p>
          </div>
          <p className="rbacct-sh">Start here</p>
          <nav className="rbacct-go" aria-label="Start here">
            {START_HERE.map((l) => (
              <a key={l.href} href={l.href} data-star={l.star ? "1" : undefined}>{l.label}</a>
            ))}
          </nav>
          {error && <p className="rbacct-error">{error}</p>}
          <div className="rbacct-actions">
            <button className="rbacct-btn rbacct-btn-primary" type="button" disabled={!!busy} onClick={() => go("rb-site-portal", {})}>
              {busy === "rb-site-portal" ? "Opening…" : "Manage billing"}
            </button>
          </div>
          <ClaraProAddon free={!!b.comp_clara_pro} addon={addon} annual={b.interval === "annual"} busy={busy} onAdd={(interval) => go("rb-site-checkout", { addon: "clara_pro", interval })} />
        </>
      ) : (
        <>
          {!onWall && <p className="rbacct-plan">
            {b.comp_tier ? <><b>{freePlanLine(b.comp_until)}</b> — everything is open.</>
              : b.level === "trial" && left !== null ? <><b>{left} {left === 1 ? "day" : "days"} left</b> in your free month</>
              : b.level === "trial" ? <b>Your free month has started</b>
              : <b>Your free month has ended</b>}
          </p>}
          {/* A free plan has nothing to buy here. The Clara Pro add-on is sold
              only on top of a PAID plan (rb-site-checkout: premium_first), so
              a free one is given from /manage/subscribers instead. */}
          {b.comp_tier ? (
            b.comp_clara_pro ? <ClaraProAddon free addon={addon} annual={annual} busy={busy} onAdd={() => undefined} /> : null
          ) : <>
          <div className="rbacct-period" role="group" aria-label="Billing period">
            <button type="button" aria-pressed={!annual} data-on={!annual ? "1" : undefined} onClick={() => setAnnual(false)}>Monthly</button>
            <button type="button" aria-pressed={annual} data-on={annual ? "1" : undefined} onClick={() => setAnnual(true)}>Yearly · one month free</button>
          </div>
          <div className="rbacct-plans" data-one="1">
            <div className="rbacct-planc">
              <b>{SITE_PLAN.name}</b>
              <span className="rbacct-price">${annual ? SITE_PLAN.annual : SITE_PLAN.monthly}<small>/{annual ? "year" : "month"}</small></span>
              <ul className="rbacct-inc">
                {SITE_PLAN.includes.map((x, i) => (
                  <li key={x} data-lead={i < 2 ? "1" : undefined}>{x}</li>
                ))}
              </ul>
              <button className="rbacct-btn rbacct-btn-primary" type="button" disabled={!!busy}
                onClick={() => go("rb-site-checkout", { interval: annual ? "annual" : "monthly" })}>
                {busy === "rb-site-checkout" ? "Opening checkout…" : "Subscribe"}
              </button>
            </div>
          </div>
          {error && <p className="rbacct-error">{error}</p>}
          <p className="rbacct-fine">Checkout is on Stripe. Cancel any time from this page.</p>
          </>}
        </>
      )}
    </section>
  );
}

/** The Clara Pro add-on, under the plan: what it is and Add, or that
 *  it's on and when it renews (Manage billing above changes or cancels it). */
function ClaraProAddon({ addon, annual, busy, onAdd, free = false }: { addon: Addon | null; annual: boolean; busy: string | null; onAdd: (interval: "monthly" | "annual") => void; free?: boolean }) {
  const on = addon && ["trialing", "active", "past_due"].includes(addon.status);
  return (
    <div className="rbacct-addon">
      <b>{CLARA_PRO_ADDON.name} add-on</b>
      {free ? (
        <span className="rbacct-what">On, free · {CLARA_PRO_ADDON.what}.</span>
      ) : on ? (
        <span className="rbacct-what">
          On · {CLARA_PRO_ADDON.what}. {addon!.cancel_at_period_end ? `Ends ${day(addon!.current_period_end)}.` : `Renews ${day(addon!.current_period_end)}.`}
        </span>
      ) : (
        <>
          <span className="rbacct-what">{CLARA_PRO_ADDON.what}, for ${annual ? CLARA_PRO_ADDON.annual : CLARA_PRO_ADDON.monthly}/{annual ? "year" : "month"} on top of {SITE_PLAN.short}.</span>
          <button className="rbacct-btn rbacct-btn-primary" type="button" disabled={!!busy} onClick={() => onAdd(annual ? "annual" : "monthly")}>
            {busy === "rb-site-checkoutclara_pro" ? "Opening checkout…" : "Add Clara Pro"}
          </button>
        </>
      )}
    </div>
  );
}
